import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { SuspectedDuplicateUserQueue } from "@/components/suspected-duplicate-user-queue";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { Switch as AriaSwitch, Heading } from "react-aria-components";
import { cx } from "@fp/ui/utils/cx";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { enableDevTools, isLocal } from "@fp/ui/env";
import { useEffect, useState } from "react";
import { useDevToolsSettings } from "@/hooks/useDevToolsSettings";
import { isAuthorizedForDevTools } from "@/lib/dev-tools-access";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@fp/ui/components/base/buttons/button";
import { Toggle } from "@fp/ui/components/base/toggle/toggle";
import { AlertCircle, RefreshCcw01, Check, Plus, Minus, LinkBroken01, Trash01, LayersThree01, PlayCircle, X } from "@untitledui/icons";
import { Dialog, Modal, ModalOverlay } from "@fp/ui/components/application/modals/modal";
import { DestructiveConfirmationModal } from "@fp/ui/components/application/modals/destructive-confirmation-modal";
import { toast } from "@/lib/toast";
import { AxiosError } from "axios";
import axios, { getErrorMessage } from "@/lib/axios";
import { BatchProcessArea, FsmPlatform, IdentityVerificationStatus, Job, SkimmerFlowKey } from "@fp/admin-api-client";
import { ComboBox } from "@fp/ui/components/base/select/combobox";
import { SelectItem } from "@fp/ui/components/base/select/select-item";
import type { Key } from "react-aria-components";
import type {
	EnrollableOpportunityDto,
	RecycleJobsHostResponse,
	SaveSuperiorPumpProgramEnrollmentRequest,
	SetSuperiorPumpProgramEnrollmentActiveRequest,
	SimulateBatchProcessRequest,
	SuperiorPumpProgramEnrollmentDto,
} from "@fp/admin-api-client";
import { usePostApiAdminDevtoolsJobsJobTrigger, useGetApiAdminDevtoolsFundingAccountBalanceCheck, getGetApiAdminDevtoolsFundingAccountBalanceCheckQueryKey, usePutApiAdminDevtoolsFundingAccountBalanceCheck, useGetApiAdminDevtoolsFsmPlatformAccess, getGetApiAdminDevtoolsFsmPlatformAccessQueryKey, usePutApiAdminDevtoolsFsmPlatformAccess, useGetApiAdminDevtoolsBusinessVerificationReview, getGetApiAdminDevtoolsBusinessVerificationReviewQueryKey, usePostApiAdminDevtoolsBusinessVerificationAttemptsClear, usePostApiAdminDevtoolsBusinessVerificationAttemptsUserRequestedByUserIdClear, usePostApiAdminDevtoolsBusinessVerificationAttemptsIpAddressClear, usePostApiAdminDevtoolsBusinessVerificationAbandon } from "@fp/admin-api-client/dev-tools/dev-tools.client";
import { useListFsmConnections } from "@fp/admin-api-client/fsm-connections/fsm-connections.client";
import {
	getGetSkimmerConnectionQueryKey,
	useCreateSkimmerConnection,
	useGetSkimmerConnection,
	useUpdateSkimmerConnection,
} from "@fp/admin-api-client/skimmer-connections/skimmer-connections.client";
import { getSettings, getGetSettingsQueryKey } from "@fp/admin-api-client/settings/settings.client";
import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import { useGetApiOnboardingProgress, getGetApiOnboardingProgressQueryKey } from "@fp/admin-api-client/onboarding-progress/onboarding-progress.client";
import { useGetOrganizationSettings, getGetOrganizationSettingsQueryKey, useUpdateOrganizationSettings } from "@fp/admin-api-client/organizations/organizations.client";
import { getListFsmPlatformsQueryKey } from "@fp/admin-api-client/fsm-platforms/fsm-platforms.client";
import { fsmPlatformLabels, fsmPlatformStatusLabels } from "@/lib/fsm-platform";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";

export const Route = createFileRoute("/dev-tools")({
	beforeLoad: async () => {
		if (enableDevTools) return;

		const [settingsResponse, userResponse] = await Promise.all([
			getSettings().catch(() => null),
			axios.get<{ type: string; value: string }[]>("/bff/user").catch(() => null),
		]);

		const showDevTools = settingsResponse?.data?.showDevTools ?? false;
		if (showDevTools) return;

		const claims = userResponse?.data;
		const email = claims?.find((c) => c.type === "email")?.value;
		const emailVerified = claims?.find((c) => c.type === "email_verified")?.value === "true";

		if (isAuthorizedForDevTools(email, emailVerified)) return;

		throw redirect({ to: "/dashboard" });
	},
	component: RouteComponent,
});

interface ApiErrorResponse {
	error?: string;
}

const SKIMMER_FLOW_LABELS: Record<SkimmerFlowKey, string> = {
	[SkimmerFlowKey.GetUsers]: "Get Users",
	[SkimmerFlowKey.ListCompletions]: "List Completions",
	[SkimmerFlowKey.GetDetail]: "Get Detail",
	[SkimmerFlowKey.GetWorkOrderTypes]: "Get Work Order Types",
};

type SkimmerEndpointDraft = { url: string; apiKey: string };

const emptySkimmerEndpoints = (): Record<SkimmerFlowKey, SkimmerEndpointDraft> =>
	Object.fromEntries(
		Object.values(SkimmerFlowKey).map((flow) => [flow, { url: "", apiKey: "" }]),
	) as Record<SkimmerFlowKey, SkimmerEndpointDraft>;

interface SuperiorPumpEnrollmentModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (opportunityId: string, request: SaveSuperiorPumpProgramEnrollmentRequest) => void;
	isLoading: boolean;
	enrollment?: SuperiorPumpProgramEnrollmentDto | null;
	enrolledOpportunityIds: string[];
}

function SuperiorPumpEnrollmentModal({ isOpen, onClose, onSubmit, isLoading, enrollment, enrolledOpportunityIds }: SuperiorPumpEnrollmentModalProps) {
	const isEditing = !!enrollment;
	const [opportunityId, setOpportunityId] = useState<Key | null>(null);
	const [serialReferenceLabel, setSerialReferenceLabel] = useState<Key | null>(null);
	const [voucherRecipientEmail, setVoucherRecipientEmail] = useState("");
	const [pumpModelNumber, setPumpModelNumber] = useState("");

	const { data: opportunities = [] } = useQuery({
		queryKey: ["superiorPumpEnrollableOpportunities"],
		enabled: isOpen,
		queryFn: async () => {
			const response = await axios.get<EnrollableOpportunityDto[]>("/api/admin/devtools/superior-pump-enrollment/opportunities");
			return response.data;
		},
	});

	const selectableOpportunities = isEditing ? opportunities : opportunities.filter((o) => !enrolledOpportunityIds.includes(o.id));
	const opportunityItems = selectableOpportunities.map((o) => ({ id: o.id, label: o.title, supportingText: o.referenceLabels.length > 0 ? `(${o.referenceLabels.join(", ")})` : undefined }));
	const selectedOpportunity = opportunities.find((o) => o.id === opportunityId);
	const referenceLabelItems = (selectedOpportunity?.referenceLabels ?? []).map((label) => ({ id: label, label }));

	useEffect(() => {
		if (!isOpen) return;
		setOpportunityId(enrollment?.opportunityId ?? null);
		setSerialReferenceLabel(enrollment?.serialReferenceLabel ?? null);
		setVoucherRecipientEmail(enrollment?.voucherRecipientEmail ?? "");
		setPumpModelNumber(enrollment?.pumpModelNumber ?? "");
	}, [isOpen, enrollment]);

	const handleClose = () => {
		if (isLoading) return;
		onClose();
	};

	const handleSubmit = () => {
		if (!opportunityId || !serialReferenceLabel) return;
		onSubmit(String(opportunityId), {
			voucherRecipientEmail: voucherRecipientEmail.trim(),
			pumpModelNumber: pumpModelNumber.trim(),
			serialReferenceLabel: String(serialReferenceLabel),
		});
	};

	const isSubmitDisabled = !opportunityId || !serialReferenceLabel || !voucherRecipientEmail.trim() || !pumpModelNumber.trim();

	return (
		<ModalOverlay isOpen={isOpen} onOpenChange={(open: boolean) => !open && handleClose()} className="items-center">
			<Modal className="max-w-md">
				<Dialog>
					<div className="bg-white rounded-lg w-full overflow-hidden">
						<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
							<Heading slot="title" level={3} className="text-lg font-semibold text-gray-900">
								{isEditing ? "Edit Enrollment" : "Enroll Opportunity"}
							</Heading>
							<Button color="tertiary" size="sm" onClick={handleClose} isDisabled={isLoading}>
								<X className="size-5" />
							</Button>
						</div>
						<div className="p-4 space-y-4">
							{isEditing ? (
								<div>
									<span className="block text-sm font-medium text-gray-700 mb-1">Opportunity</span>
									<p className="text-sm text-gray-900">{enrollment.opportunityTitle}</p>
								</div>
							) : (
								<div>
									<ComboBox
										label="Opportunity"
										placeholder="Search opportunities"
										shortcut={false}
										items={opportunityItems}
										selectedKey={opportunityId}
										onSelectionChange={setOpportunityId}
										isDisabled={isLoading}
									>
										{(item) => <SelectItem id={item.id} textValue={item.label} supportingText={item.supportingText}>{item.label}</SelectItem>}
									</ComboBox>
									<p className="mt-1 text-xs text-gray-500">Already-enrolled opportunities are not listed. Use Edit on the card to change one.</p>
								</div>
							)}
							<ComboBox
								label="Serial reference"
								placeholder={selectedOpportunity ? "Which reference holds the serial" : "Choose an opportunity first"}
								shortcut={false}
								isDisabled={isLoading || !selectedOpportunity}
								items={referenceLabelItems}
								selectedKey={serialReferenceLabel}
								onSelectionChange={setSerialReferenceLabel}
							>
								{(item) => <SelectItem id={item.id} textValue={item.label}>{item.label}</SelectItem>}
							</ComboBox>
							{selectedOpportunity && referenceLabelItems.length === 0 && (
								<p className="text-sm text-gray-600">This opportunity has no Proof of Work reference requirements. There is nowhere to read a serial from. Add one to the opportunity first.</p>
							)}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Voucher recipient email</label>
								<input
									type="email"
									value={voucherRecipientEmail}
									onChange={(e) => setVoucherRecipientEmail(e.target.value)}
									placeholder="name@company.com"
									className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
									disabled={isLoading}
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Pump model number</label>
								<input
									type="text"
									value={pumpModelNumber}
									onChange={(e) => setPumpModelNumber(e.target.value)}
									placeholder="98300"
									className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
									disabled={isLoading}
								/>
							</div>
						</div>
						<div className="flex gap-3 px-4 py-3 border-t border-gray-200">
							<Button color="secondary" className="flex-1" onClick={handleClose} isDisabled={isLoading}>Cancel</Button>
							<Button color="primary" className="flex-1" onClick={handleSubmit} isDisabled={isSubmitDisabled} isLoading={isLoading}>{isEditing ? "Save" : "Enroll"}</Button>
						</div>
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
}

function SuperiorPumpEnrollment() {
	const queryClient = useQueryClient();
	const [editing, setEditing] = useState<SuperiorPumpProgramEnrollmentDto | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [removing, setRemoving] = useState<SuperiorPumpProgramEnrollmentDto | null>(null);
	const enrollmentsQueryKey = ["superiorPumpEnrollments"] as const;

	const { data: enrollments = [] } = useQuery({
		queryKey: enrollmentsQueryKey,
		queryFn: async () => {
			const response = await axios.get<SuperiorPumpProgramEnrollmentDto[]>("/api/admin/devtools/superior-pump-enrollment");
			return response.data;
		},
	});

	const invalidate = () => queryClient.invalidateQueries({ queryKey: enrollmentsQueryKey });

	const onError = (fallback: string) => (err: AxiosError<ApiErrorResponse>) => {
		toast.error(err.response?.data?.error || fallback);
	};

	const saveMutation = useMutation({
		mutationFn: async ({ opportunityId, request }: { opportunityId: string; request: SaveSuperiorPumpProgramEnrollmentRequest }) => {
			await axios.put<SuperiorPumpProgramEnrollmentDto>(`/api/admin/devtools/superior-pump-enrollment/${opportunityId}`, request);
		},
		onSuccess: () => {
			invalidate();
			setIsModalOpen(false);
			setEditing(null);
			toast.success("Enrollment saved");
		},
		onError: onError("Failed to save enrollment"),
	});

	const setActiveMutation = useMutation({
		mutationFn: async ({ opportunityId, isActive }: { opportunityId: string; isActive: boolean }) => {
			const payload: SetSuperiorPumpProgramEnrollmentActiveRequest = { isActive };
			await axios.put<SuperiorPumpProgramEnrollmentDto>(`/api/admin/devtools/superior-pump-enrollment/${opportunityId}/active`, payload);
		},
		onSuccess: (_, { isActive }) => {
			invalidate();
			toast.success(isActive ? "Enrollment enabled" : "Enrollment disabled");
		},
		onError: onError("Failed to change enrollment state"),
	});

	const removeMutation = useMutation({
		mutationFn: async (opportunityId: string) => {
			await axios.delete(`/api/admin/devtools/superior-pump-enrollment/${opportunityId}`);
		},
		onSuccess: () => {
			invalidate();
			setRemoving(null);
			toast.success("Enrollment removed");
		},
		onError: onError("Failed to remove enrollment"),
	});

	const isBusy = setActiveMutation.isPending || removeMutation.isPending;

	const openAdd = () => {
		setEditing(null);
		setIsModalOpen(true);
	};

	const openEdit = (enrollment: SuperiorPumpProgramEnrollmentDto) => {
		setEditing(enrollment);
		setIsModalOpen(true);
	};

	return (
		<section className="border border-gray-200 rounded-lg p-4 bg-white">
			<h4 className="font-semibold text-gray-900 mb-2">Superior Pump Program</h4>
			<p className="text-sm text-gray-600 mb-4">Enrolled opportunities email the partner an install confirmation when their payout requests are approved.</p>
			{enrollments.length === 0 ? (
				<p className="text-sm text-gray-500 italic mb-4">No opportunities enrolled.</p>
			) : (
				<div className="space-y-2 mb-4">
					{enrollments.map((item) => (
						<div key={item.opportunityId} className="flex flex-col gap-3 p-3 bg-gray-50 rounded-lg">
							<div className="flex items-start justify-between gap-2">
								<span className="text-sm font-medium text-gray-900">{item.opportunityTitle}</span>
								<span className={cx("shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold", item.isActive ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700")}>
									{item.isActive ? "Active" : "Disabled"}
								</span>
							</div>
							<dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
								<dt className="text-gray-500">Model</dt>
								<dd className="text-gray-900 break-words">{item.pumpModelNumber}</dd>
								<dt className="text-gray-500">Serial reference</dt>
								<dd className="text-gray-900 break-words">{item.serialReferenceLabel}</dd>
								<dt className="text-gray-500">Voucher email</dt>
								<dd className="text-gray-900 break-all">{item.voucherRecipientEmail}</dd>
							</dl>
							<div className="flex flex-wrap gap-2">
								<Button onClick={() => openEdit(item)} color="secondary" size="sm" isDisabled={isBusy}>Edit</Button>
								<Button onClick={() => setActiveMutation.mutate({ opportunityId: item.opportunityId, isActive: !item.isActive })} color="secondary" size="sm" isDisabled={isBusy} iconLeading={item.isActive ? Minus : Plus}>
									{item.isActive ? "Disable" : "Enable"}
								</Button>
								<Button onClick={() => setRemoving(item)} color="secondary-destructive" size="sm" isDisabled={isBusy} iconLeading={Trash01}>Remove</Button>
							</div>
						</div>
					))}
				</div>
			)}
			<Button onClick={openAdd} color="secondary" size="sm" isDisabled={isBusy} iconLeading={Plus}>Enroll Opportunity</Button>
			<SuperiorPumpEnrollmentModal
				isOpen={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setEditing(null);
				}}
				onSubmit={(opportunityId, request) => saveMutation.mutate({ opportunityId, request })}
				isLoading={saveMutation.isPending}
				enrollment={editing}
				enrolledOpportunityIds={enrollments.map((item) => item.opportunityId)}
			/>
			<DestructiveConfirmationModal
				isOpen={!!removing}
				onClose={() => setRemoving(null)}
				onConfirm={() => removing && removeMutation.mutate(removing.opportunityId)}
				title="Remove Enrollment"
				entity={{ label: removing?.opportunityTitle ?? "", description: removing ? `Model ${removing.pumpModelNumber} · ${removing.voucherRecipientEmail}` : undefined }}
				impactText="The opportunity will stop emailing the partner. Its enrollment details will be lost. To pause the program without losing them, use Disable instead."
				confirmText="Remove"
				isLoading={removeMutation.isPending}
			/>
		</section>
	);
}

function SkimmerManualConnection() {
	const queryClient = useQueryClient();
	const { data: connectionsResponse } = useListFsmConnections();
	const connection = (connectionsResponse?.data ?? []).find(
		(c) => c.platform === FsmPlatform.Skimmer,
	);
	const connectionId = connection?.id;
	const pairedConnectionId = connection?.isConnected ? connection.id : undefined;

	const { data: existingResponse } = useGetSkimmerConnection(connectionId ?? "", {
		query: {
			queryKey: getGetSkimmerConnectionQueryKey(connectionId ?? ""),
			enabled: !!connectionId,
		},
	});
	const existingFlows = existingResponse?.data?.flows;

	const [instanceId, setInstanceId] = useState("");
	const [endpoints, setEndpoints] = useState<Record<SkimmerFlowKey, SkimmerEndpointDraft>>(emptySkimmerEndpoints);

	useEffect(() => {
		if (existingFlows) {
			setEndpoints({
				[SkimmerFlowKey.GetUsers]: { url: existingFlows.getUsersUrl, apiKey: "" },
				[SkimmerFlowKey.ListCompletions]: { url: existingFlows.listCompletionsUrl, apiKey: "" },
				[SkimmerFlowKey.GetDetail]: { url: existingFlows.getDetailUrl, apiKey: "" },
				[SkimmerFlowKey.GetWorkOrderTypes]: { url: existingFlows.getWorkOrderTypesUrl, apiKey: "" },
			});
		}
	}, [existingFlows]);

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: getGetSkimmerConnectionQueryKey(connectionId ?? "") });
	};

	const onError = (err: AxiosError<ApiErrorResponse>) => {
		toast.error(err.response?.data?.error || "Failed to save Skimmer connection");
	};

	const createMutation = useCreateSkimmerConnection({
		mutation: {
			onSuccess: () => {
				toast.success("Skimmer connection created");
				invalidate();
			},
			onError,
		},
	});

	const updateMutation = useUpdateSkimmerConnection({
		mutation: {
			onSuccess: () => {
				toast.success("Skimmer connection updated");
				invalidate();
			},
			onError,
		},
	});

	const setField = (flow: SkimmerFlowKey, field: keyof SkimmerEndpointDraft, value: string) => {
		setEndpoints((prev) => ({ ...prev, [flow]: { ...prev[flow], [field]: value } }));
	};

	const isBusy = createMutation.isPending || updateMutation.isPending;
	const isComplete =
		instanceId.trim().length > 0 &&
		Object.values(SkimmerFlowKey).every(
			(flow) => endpoints[flow].url.trim().length > 0 && endpoints[flow].apiKey.trim().length > 0,
		);

	const handleSave = () => {
		const data = {
			prismaticInstanceId: instanceId.trim(),
			flows: Object.values(SkimmerFlowKey).map((flow) => ({
				flow,
				url: endpoints[flow].url.trim(),
				apiKey: endpoints[flow].apiKey.trim(),
			})),
		};

		if (pairedConnectionId) {
			updateMutation.mutate({ id: pairedConnectionId, data });
		} else {
			createMutation.mutate({ data });
		}
	};

	return (
		<section className="border border-gray-200 rounded-lg p-4 bg-white">
			<h4 className="font-semibold text-gray-900 mb-2">Skimmer Manual Connection</h4>
			<p className="text-sm text-gray-600 mb-4">
				Operates on the current organization. Bypasses the pairing flow — set the Prismatic instance id and each flow's URL and API key directly. {pairedConnectionId ? "Updates the existing connection." : "Pairs the connection."}
			</p>
			<div className="space-y-3 mb-4">
				<label className="flex flex-col gap-1">
					<span className="text-xs font-medium text-gray-700">Prismatic instance id</span>
					<input type="text" value={instanceId} onChange={(e) => setInstanceId(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
				</label>
				{Object.values(SkimmerFlowKey).map((flow) => (
					<div key={flow} className="flex flex-col gap-2 p-3 border border-gray-200 rounded-lg">
						<span className="text-sm font-medium text-gray-900">{SKIMMER_FLOW_LABELS[flow]}</span>
						<label className="flex flex-col gap-1">
							<span className="text-xs font-medium text-gray-700">URL</span>
							<input type="text" value={endpoints[flow].url} onChange={(e) => setField(flow, "url", e.target.value)} placeholder="https://hooks.prismatic.io/trigger/..." className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
						</label>
						<label className="flex flex-col gap-1">
							<span className="text-xs font-medium text-gray-700">API key</span>
							<input type="password" value={endpoints[flow].apiKey} onChange={(e) => setField(flow, "apiKey", e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
						</label>
					</div>
				))}
			</div>
			<Button onClick={handleSave} color="secondary" size="sm" isLoading={isBusy} isDisabled={isBusy || !isComplete} iconLeading={Check}>{connectionId ? "Update Connection" : "Create Connection"}</Button>
			<div className="flex items-start gap-2 p-3 mt-4 bg-amber-50 border border-amber-200 rounded-lg">
				<AlertCircle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
				<p className="text-sm text-amber-800">API keys are never returned by the API. They always start blank — re-enter them on every edit. Saving verifies the flows before persisting.</p>
			</div>
		</section>
	);
}

interface JobCopy {
	label: string;
	description: string;
	requiresConfirmation: boolean;
}

const JOBS: Record<Job, JobCopy> = {
	[Job.AddressGeocodeBackfill]: {
		label: "Address Geocode Backfill",
		description: "Selects addresses with no geocode and queues one geocoding request each, bounded by the configured batch size.",
		requiresConfirmation: false,
	},
	[Job.AstraAccountStatusPoll]: {
		label: "Astra Account Status Poll",
		description: "Queues a status refresh for every Astra bank account still pending inside the poll window. Accounts abandoned beyond the escalation window raise an alert.",
		requiresConfirmation: false,
	},
	[Job.AstraTokenRefresh]: {
		label: "Astra Token Refresh",
		description: "Selects Astra OAuth credentials whose refresh token is at or past the expiry threshold and queues a token refresh for each. Does nothing while token refresh is disabled.",
		requiresConfirmation: false,
	},
	[Job.AutoTopUp]: {
		label: "Auto Top-Up",
		description: "Checks every organization with auto top-up enabled and queues a deposit for any whose projected balance is under its threshold. Real money moves — the deposit is an ACH transfer from the organization's funding source. Triggering twice is safe because a pending deposit already counts toward the projected balance.",
		requiresConfirmation: true,
	},
	[Job.BalanceTransferReconciliation]: {
		label: "Balance Transfer Reconciliation",
		description: "Selects balance transfers left processing past the staleness threshold and queues a reconciliation for each. Reconciliation reads the transfer's settled status from Astra and applies it here, finishing transfers whose webhook never arrived. Nothing new is sent to Astra.",
		requiresConfirmation: false,
	},
	[Job.BatchProcessResume]: {
		label: "Batch Process Resume",
		description: "Selects batch processes with unfinished items and no activity within the stall window and queues a resume for each. A stalled bulk approval issues its unfinished approvals again. A stalled sync now discovers its window again and sends its unfinished completed work. A stalled preview or simulation records its unfinished items as failed. Approvals lead to payouts. Real money can move.",
		requiresConfirmation: true,
	},
	[Job.DepositPlanning]: {
		label: "Deposit Planning",
		description: "Queues deposit planning for every organization that funds on a schedule. An organization on recurring deposits keeps its window full: the next occurrence days are planned until the configured number of deposits are scheduled ahead. An organization on just-in-time deposits recomputes its cashflow projection, plans a deposit for every payday the balance cannot cover, resizes or cancels the ones already planned and tells the admins when the maximum deposit leaves a payday short. Real money moves when a planned deposit's day arrives. Triggering twice is safe because planning only moves deposits to what the schedule or the projection needs.",
		requiresConfirmation: true,
	},
	[Job.DisbursementRun]: {
		label: "Disbursement Run",
		description: "Selects every payee awaiting payment: a released payout with a balance owing or a scheduled payout that has reached its release date. Queues a consolidation for each payee. A consolidation emits one payment for everything the organization's cleared balance can fund from the oldest payout forward. Real money moves.",
		requiresConfirmation: true,
	},
	[Job.FailedDepositReconciliation]: {
		label: "Failed Deposit Reconciliation",
		description: "Selects deposits that failed recently without ever receiving a provider transaction id and queues a reconciliation for each. Reconciliation reads the deposit's status from Astra and applies it here, recovering deposits that failed locally but went through. Nothing new is sent to Astra.",
		requiresConfirmation: false,
	},
	[Job.EmailVerificationPurge]: {
		label: "Email Verification Purge",
		description: "Selects email verifications whose code has expired and queues deletion of each one. A person who still wants the email adds it again and gets a new code. Verified emails are untouched.",
		requiresConfirmation: false,
	},
	[Job.FsmCompletedWorkRetry]: {
		label: "FSM Completed Work Retry",
		description: "Queues another attempt for failed completed work that is due for retry on active connections. Admins are notified about completed work that has been abandoned.",
		requiresConfirmation: false,
	},
	[Job.FsmSyncPast24HoursEveryHour]: {
		label: "FSM Sync — Past 24 Hours",
		description: "Queues a sync for every active FSM connection over the past 24 hours. Catches a job priced or recategorized after the one-hour sweep stopped looking at it. The change lands the same day instead of overnight. Runs hourly.",
		requiresConfirmation: false,
	},
	[Job.FsmSyncPast7DaysEveryDay]: {
		label: "FSM Sync — Past 7 Days",
		description: "Queues a sync for every active FSM connection over the past 7 days. The heaviest of the three sweeps — it pulls far more from each platform and consumes more of the upstream rate limit budget. Runs nightly.",
		requiresConfirmation: false,
	},
	[Job.FsmSyncPastHourEvery5Minutes]: {
		label: "FSM Sync — Past Hour",
		description: "Queues a sync for every active FSM connection over the past hour. The lightest of the three sweeps, catching a job within minutes of the technician finishing it. Runs every 5 minutes.",
		requiresConfirmation: false,
	},
	[Job.FsmTechInvitation]: {
		label: "FSM Tech Invitation",
		description: "Selects payable techs on each FSM connection's roster who have not signed up and queues an invitation for each. Real email reaches real people, including from the dev environment.",
		requiresConfirmation: true,
	},
	[Job.FsmTechSync]: {
		label: "FSM Tech Sync",
		description: "Queues every FSM connection with sync on to sync its techs. New techs are recorded, known ones are updated and anyone missing is marked as gone. Runs every 5 minutes.",
		requiresConfirmation: false,
	},
	[Job.IdentityVerificationAstraBackfill]: {
		label: "Identity Verification Entity Type Backfill",
		description: "Selects approved identity verifications with no entity type and queues a user profile backfill for each.",
		requiresConfirmation: false,
	},
	[Job.IdentityVerificationStatusRecovery]: {
		label: "Identity Verification Status Recovery",
		description: "Selects pending identity verifications past their retry time and queues another Astra status check for each.",
		requiresConfirmation: false,
	},
	[Job.IdentityVerificationSuspensionRecheck]: {
		label: "Identity Verification Suspension Recheck",
		description: "Selects every suspended identity verification and queues an Astra status check for each. A suspension Astra has since lifted is lifted here too. A missed webhook never leaves one in place. Triggering again only checks again.",
		requiresConfirmation: false,
	},
	[Job.LowBalanceCheck]: {
		label: "Low Balance Check",
		description: "Checks organizations with low balance notifications enabled and auto top-up off, then queues a notification for any under threshold. Real email reaches real people. Organizations back above threshold have their warning counter cleared.",
		requiresConfirmation: true,
	},
	[Job.OrganizationAddressBackfill]: {
		label: "Organization Address Backfill",
		description: "Selects organizations that have a business profile but no address and queues an address backfill for each, sourcing the address from Astra.",
		requiresConfirmation: false,
	},
	[Job.OrglessAccountDetection]: {
		label: "Orgless Account Detection",
		description: "Selects accounts older than the configured minimum age holding a linked bank account with no relationship to a funded organization and no payouts. Queues each to be flagged. Already-flagged accounts are skipped. Triggering again raises no second alert.",
		requiresConfirmation: false,
	},
	[Job.OverduePayoutExpiration]: {
		label: "Overdue Payout Expiration",
		description: "Selects payouts that have been overdue for the whole expiration window and queues an expiration for each. Expiring a payout fails it and tells the payee and the payer's administrators. Nothing is sent to Astra. An administrator can retry a failed payout from its page.",
		requiresConfirmation: true,
	},
	[Job.PaymentReconciliation]: {
		label: "Payment Reconciliation",
		description: "Selects Astra payments left processing past the staleness threshold and every unconfirmed payment, then queues a reconciliation for each. Reconciliation reads a processing payment's routine from Astra and settles or fails the payment here, finishing payments whose webhook never arrived. For an unconfirmed payment it looks the routine up by the payment's reference and adopts it or fails the payment. A failed payment sends its payout down the payment failure path, which can retry the payout. Real money can move.",
		requiresConfirmation: true,
	},
	[Job.PendingPayoutDigest]: {
		label: "Pending Payout Digest",
		description: "Checks organizations with the pending payout digest enabled and queues a digest for any currently inside its configured send window. Outside that window nothing is queued.",
		requiresConfirmation: false,
	},
	[Job.RealTimePaymentEligibilityRecheck]: {
		label: "Real-Time Payment Eligibility Recheck",
		description: "Selects every approved bank account not known to be real-time whose owner has an organization relationship and queues a real-time eligibility check with Astra for each. An account Astra reports real-time leaves the set.",
		requiresConfirmation: false,
	},
	[Job.ReportExportFileCleanup]: {
		label: "Report Export File Cleanup",
		description: "Selects invoice, earnings and payout report exports untouched beyond the retention window and queues deletion of their generated files. The export records stay and the next download renders the files again.",
		requiresConfirmation: false,
	},
	[Job.RosterEmailCollisionScan]: {
		label: "Roster Email Collision Scan",
		description: "Selects linked talent members whose roster email is the primary email of a different user and queues an observation for each. An observation raises or refreshes a suspected duplicate user between the two. Triggering again adds no second row.",
		requiresConfirmation: false,
	},
	[Job.ScheduledDepositInitiation]: {
		label: "Scheduled Deposit Initiation",
		description: "Selects scheduled deposits whose initiation date has arrived and queues an initiation for each. Initiating a deposit makes it an ordinary deposit: an ACH transfer from the chosen bank account into the organization's FieldPay balance. Real money moves. Triggering twice is safe because a deposit initiates once.",
		requiresConfirmation: true,
	},
	[Job.ScheduledPayoutDigest]: {
		label: "Scheduled Payout Digest",
		description: "Selects payees with scheduled payouts not yet announced that were scheduled before 5 PM in their organization's time zone and queues one digest for each. A payee is told about a payout once. Triggering again queues nothing new.",
		requiresConfirmation: false,
	},
	[Job.SignupPurge]: {
		label: "Signup Purge",
		description: "Selects signups that expired before anyone completed the sign-in link or code and queues deletion of each one. A person who still wants to join signs in again and gets a new signup. Accounts are untouched.",
		requiresConfirmation: false,
	},
	[Job.StaleConsumerGroupPrune]: {
		label: "Stale Consumer Group Prune",
		description: "Queues a prune of Redis consumer groups that no running consumer backs any more.",
		requiresConfirmation: false,
	},
	[Job.StrandedPaymentRecovery]: {
		label: "Stranded Payment Recovery",
		description: "Selects organizations with payments stranded in Pending past the staleness threshold and queues a routine sweep for each. Payments whose Astra routine already exists are reconnected to it; the rest are re-executed. Real money moves.",
		requiresConfirmation: true,
	},
	[Job.StreamRecovery]: {
		label: "Stream Recovery",
		description: "Reclaims Redis stream messages left pending beyond the idle threshold and redelivers them. Messages past the retry limit are dead-lettered instead.",
		requiresConfirmation: false,
	},
	[Job.WeeklyEarningsDigest]: {
		label: "Weekly Earnings Digest",
		description: "Checks organizations with the weekly earnings digest enabled and queues a digest for any whose configured day and send window is now. Sends nothing outside that window or within seven days of the last send.",
		requiresConfirmation: false,
	},
};

function JobTriggers() {
	const [selectedJob, setSelectedJob] = useState<Key | null>(null);
	const [isConfirming, setIsConfirming] = useState(false);

	const job = selectedJob ? (selectedJob as Job) : null;
	const copy = job ? JOBS[job] : null;

	const jobItems = Object.entries(JOBS)
		.map(([id, item]) => ({ id, label: item.label }))
		.sort((a, b) => a.label.localeCompare(b.label));

	const triggerMutation = usePostApiAdminDevtoolsJobsJobTrigger({
		mutation: {
			onSuccess: () => {
				setIsConfirming(false);
				toast.success(`${copy?.label} queued`, { description: "It should run shortly." });
			},
			onError: (err: AxiosError<ApiErrorResponse>) => {
				toast.error(err.response?.data?.error || "Failed to queue the job");
			},
		},
	});

	const trigger = () => job && triggerMutation.mutate({ job });

	return (
		<section className="border border-gray-200 rounded-lg p-4 bg-white">
			<h4 className="font-semibold text-gray-900 mb-2">Jobs</h4>
			<p className="text-sm text-gray-600 mb-4">Run a background job now instead of waiting for its cron. Triggering runs exactly what the cron runs, against the whole environment rather than your organization alone.</p>
			<div className="mb-4">
				<ComboBox
					label="Job"
					placeholder="Search jobs"
					shortcut={false}
					items={jobItems}
					selectedKey={selectedJob}
					onSelectionChange={setSelectedJob}
					isDisabled={triggerMutation.isPending}
				>
					{(item) => <SelectItem id={item.id} textValue={item.label}>{item.label}</SelectItem>}
				</ComboBox>
			</div>
			{copy && (
				<div className={cx("flex items-start gap-2 p-3 mb-4 rounded-lg border", copy.requiresConfirmation ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200")}>
					{copy.requiresConfirmation && <AlertCircle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />}
					<p className={cx("text-sm", copy.requiresConfirmation ? "text-amber-800" : "text-gray-700")}>{copy.description}</p>
				</div>
			)}
			<Button
				onClick={() => (copy?.requiresConfirmation ? setIsConfirming(true) : trigger())}
				color={copy?.requiresConfirmation ? "secondary-destructive" : "secondary"}
				size="sm"
				isLoading={triggerMutation.isPending}
				isDisabled={!job || triggerMutation.isPending}
				iconLeading={PlayCircle}
			>
				Trigger Job
			</Button>
			<DestructiveConfirmationModal
				isOpen={isConfirming}
				onClose={() => setIsConfirming(false)}
				onConfirm={trigger}
				title="Trigger Job"
				entity={{ label: copy?.label ?? "", description: "Background job" }}
				impactText={copy?.description ?? ""}
				confirmText="Trigger"
				isLoading={triggerMutation.isPending}
			/>
		</section>
	);
}

function FundingAccountBalanceCheck() {
	const queryClient = useQueryClient();
	const { data: fundingAccountBalanceCheckResponse, isLoading } = useGetApiAdminDevtoolsFundingAccountBalanceCheck();

	const setMutation = usePutApiAdminDevtoolsFundingAccountBalanceCheck({
		mutation: {
			onSuccess: (response) => {
				queryClient.setQueryData(getGetApiAdminDevtoolsFundingAccountBalanceCheckQueryKey(), response);
				toast.success(response.data.enabled ? "Funding account balance check on" : "Funding account balance check off");
			},
			onError: (err) => {
				toast.error(getErrorMessage(err, "Failed to change the funding account balance check"));
			},
		},
	});

	return (
		<section className="border border-gray-200 rounded-lg p-4 bg-white">
			<h4 className="font-semibold text-gray-900 mb-2">Funding Account Balance Check</h4>
			<p className="text-sm text-gray-600 mb-4">Before every deposit FieldPay reads the funding account's real-time balance through Plaid and aborts a deposit the account cannot cover. Each read costs $0.10.</p>
			<Toggle
				size="sm"
				className="w-full"
				label="Check the balance before every deposit"
				hint="Turn it off to initiate this organization's deposits without reading the balance."
				isSelected={fundingAccountBalanceCheckResponse?.data.enabled ?? true}
				isDisabled={isLoading || setMutation.isPending}
				onChange={(enabled) => setMutation.mutate({ data: { enabled } })}
			/>
			<div className="flex items-start gap-2 p-3 mt-4 bg-amber-50 border border-amber-200 rounded-lg">
				<AlertCircle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
				<p className="text-sm text-amber-800">This applies to the current organization in every environment, production included. With the check off, a deposit the account cannot cover is initiated and returned by the bank.</p>
			</div>
		</section>
	);
}

function RecycleJobsHost() {
	const [isConfirming, setIsConfirming] = useState(false);

	const recycleMutation = useMutation({
		mutationFn: async () => {
			const response = await axios.post<RecycleJobsHostResponse>("/api/admin/devtools/recycle-jobs-host");
			return response.data;
		},
		onSuccess: () => {
			setIsConfirming(false);
			toast.success("Jobs.Host recycle requested", { description: "The team is emailed the Octopus runbook run once it starts." });
		},
		onError: (err: AxiosError<ApiErrorResponse>) => {
			toast.error(err.response?.data?.error || "Failed to request the Jobs.Host recycle");
		},
	});

	return (
		<section className="border border-gray-200 rounded-lg p-4 bg-white">
			<h4 className="font-semibold text-gray-900 mb-2">Recycle Jobs.Host</h4>
			<p className="text-sm text-gray-600 mb-4">Restarts Jobs.Host through the Octopus runbook — the same recovery the lag monitor triggers automatically when a Redis stream listener dies. Use this when a stream has stopped consuming and nobody with Octopus access is around.</p>
			<Button onClick={() => setIsConfirming(true)} color="secondary-destructive" size="sm" isDisabled={recycleMutation.isPending} iconLeading={RefreshCcw01}>Recycle Jobs.Host</Button>
			<div className="flex items-start gap-2 p-3 mt-4 bg-amber-50 border border-amber-200 rounded-lg">
				<AlertCircle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
				<p className="text-sm text-amber-800">This restarts the host for the whole environment, not just your organization. In-flight background work is interrupted and retried.</p>
			</div>
			<DestructiveConfirmationModal
				isOpen={isConfirming}
				onClose={() => setIsConfirming(false)}
				onConfirm={() => recycleMutation.mutate()}
				title="Recycle Jobs.Host"
				entity={{ label: "Jobs.Host", description: "Background job and queue processing host" }}
				impactText="Every background processor in this environment restarts. Queue and stream work in flight is interrupted and redelivered. The recycle is recorded against your name and emailed to the team."
				confirmText="Recycle"
				isLoading={recycleMutation.isPending}
			/>
		</section>
	);
}

function BusinessVerificationReview() {
	const queryClient = useQueryClient();
	const { data: reviewResponse } = useGetApiAdminDevtoolsBusinessVerificationReview();
	const review = reviewResponse?.data;

	const invalidate = () => queryClient.invalidateQueries({ queryKey: getGetApiAdminDevtoolsBusinessVerificationReviewQueryKey() });

	const onCleared = (count: number) => {
		invalidate();
		toast.success(`Cleared ${count} business verification attempt${count === 1 ? "" : "s"}`);
	};

	const onClearFailed = (err: AxiosError<ApiErrorResponse>) => {
		toast.error(err.response?.data?.error || "Failed to clear the business verification attempts");
	};

	const clearOrganizationMutation = usePostApiAdminDevtoolsBusinessVerificationAttemptsClear({
		mutation: { onSuccess: (response) => onCleared(response.data), onError: onClearFailed },
	});

	const clearUserMutation = usePostApiAdminDevtoolsBusinessVerificationAttemptsUserRequestedByUserIdClear({
		mutation: { onSuccess: (response) => onCleared(response.data), onError: onClearFailed },
	});

	const clearIpAddressMutation = usePostApiAdminDevtoolsBusinessVerificationAttemptsIpAddressClear({
		mutation: { onSuccess: (response) => onCleared(response.data), onError: onClearFailed },
	});

	const abandonVerificationMutation = usePostApiAdminDevtoolsBusinessVerificationAbandon({
		mutation: {
			onSuccess: () => {
				invalidate();
				toast.success("Abandoned the pending business verification");
			},
			onError: (err: AxiosError<ApiErrorResponse>) => {
				toast.error(err.response?.data?.error || "Failed to abandon the business verification");
			},
		},
	});

	const isMutating = clearOrganizationMutation.isPending || clearUserMutation.isPending || clearIpAddressMutation.isPending || abandonVerificationMutation.isPending;

	return (
		<section className="border border-gray-200 rounded-lg p-4 bg-white">
			<h4 className="font-semibold text-gray-900 mb-2">Business Verification</h4>
			<p className="text-sm text-gray-600 mb-4">Uncleared business verification attempts for this organization. Past its limit the organization cannot start another verification until the attempts are cleared. Clearing a requesting user or a client IP lifts that limit at every organization they reached. Abandoning a pending verification lets the organization start over without touching its bank accounts.</p>
			{review && (
				<div className="flex flex-col gap-3">
					<div className="flex flex-wrap gap-1">
						{review.verificationStatus ? (
							<span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{review.verificationStatus}</span>
						) : (
							<span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">Not started</span>
						)}
						{review.orphanedBusinessProfileCount > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">{review.orphanedBusinessProfileCount} orphaned profile{review.orphanedBusinessProfileCount === 1 ? "" : "s"}</span>}
					</div>
					{review.attemptCount === 0 ? (
						<p className="text-sm text-gray-500 italic">No uncleared attempts.</p>
					) : (
						<div className="flex flex-col gap-2 p-2 bg-gray-50 rounded-lg">
							<span className="text-xs text-gray-500">
								{review.attemptCount} attempt{review.attemptCount === 1 ? "" : "s"} · {review.paidAttemptCount} paid
								{review.firstAttemptedAt ? ` · first ${formatLocalDate(review.firstAttemptedAt, "datetimeWithYear")}` : ""}
								{review.lastAttemptedAt ? ` · last ${formatLocalDate(review.lastAttemptedAt, "datetimeWithYear")}` : ""}
							</span>
							<div className="flex flex-wrap items-center gap-2">
								<span className="text-xs text-gray-500">Requested by</span>
								{review.requesters.map((requester) => (
									<span key={requester.requestedByUserId} className="inline-flex items-center gap-1 text-xs text-gray-700">
										{requester.requestedByUserId}
										{requester.isSharedWithAnotherOrganization && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Also at another organization</span>}
										<Button
											onClick={() => clearUserMutation.mutate({ requestedByUserId: requester.requestedByUserId })}
											color="secondary"
											size="sm"
											isLoading={clearUserMutation.isPending && clearUserMutation.variables?.requestedByUserId === requester.requestedByUserId}
											isDisabled={isMutating}
											iconLeading={Check}
										>
											Clear
										</Button>
									</span>
								))}
							</div>
							<div className="flex flex-wrap items-center gap-2">
								<span className="text-xs text-gray-500">From</span>
								{review.ipAddresses.map((ipAddress) => (
									<span key={ipAddress.ipAddress} className="inline-flex items-center gap-1 text-xs text-gray-700">
										{ipAddress.ipAddress}
										{ipAddress.isSharedWithAnotherOrganization && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Also at another organization</span>}
										<Button
											onClick={() => clearIpAddressMutation.mutate({ data: { ipAddress: ipAddress.ipAddress } })}
											color="secondary"
											size="sm"
											isLoading={clearIpAddressMutation.isPending && clearIpAddressMutation.variables?.data.ipAddress === ipAddress.ipAddress}
											isDisabled={isMutating}
											iconLeading={Check}
										>
											Clear
										</Button>
									</span>
								))}
							</div>
						</div>
					)}
					<div className="flex gap-2">
						{review.verificationStatus === IdentityVerificationStatus.Pending && (
							<Button onClick={() => abandonVerificationMutation.mutate()} color="secondary-destructive" size="sm" isLoading={abandonVerificationMutation.isPending} isDisabled={isMutating} iconLeading={X}>Abandon verification</Button>
						)}
						{review.attemptCount > 0 && (
							<Button onClick={() => clearOrganizationMutation.mutate()} color="secondary" size="sm" isLoading={clearOrganizationMutation.isPending} isDisabled={isMutating} iconLeading={Check}>Clear attempts</Button>
						)}
					</div>
				</div>
			)}
		</section>
	);
}

type SimPreset = { name: string; hint: string; config: SimulateBatchProcessRequest };

const simPresets: SimPreset[] = [
	{
		name: "Window absorbed",
		hint: "Ten items through a 5-permit window sliding every 4 seconds under a 5-second wait budget. The second five wait out one slide in process and nothing defers.",
		config: {
			itemCount: 10,
			minDelayMilliseconds: 100,
			maxDelayMilliseconds: 300,
			failCount: 0,
			area: BatchProcessArea.PayoutRequest,
			name: "Preset: window absorbed",
			rateLimit: {
				window: { permitLimit: 5, windowMilliseconds: 4000 },
				waitTimeoutMilliseconds: 5000,
				useInProcessRateLimiter: false,
			},
		},
	},
	{
		name: "Window deferred",
		hint: "The same window under a 1-second wait budget. Denials outlive the budget and defer on the window profile.",
		config: {
			itemCount: 30,
			minDelayMilliseconds: 100,
			maxDelayMilliseconds: 300,
			failCount: 0,
			area: BatchProcessArea.PayoutRequest,
			name: "Preset: window deferred",
			rateLimit: {
				window: { permitLimit: 5, windowMilliseconds: 4000 },
				waitTimeoutMilliseconds: 1000,
				useInProcessRateLimiter: false,
			},
		},
	},
	{
		name: "Pacing deferred",
		hint: "A bucket of 5 tokens every 3 seconds under a 1-second wait budget. The first burst clears and the rest defer on the pacing profile.",
		config: {
			itemCount: 40,
			minDelayMilliseconds: 100,
			maxDelayMilliseconds: 300,
			failCount: 0,
			area: BatchProcessArea.PayoutRequest,
			name: "Preset: pacing deferred",
			rateLimit: {
				waitTimeoutMilliseconds: 1000,
				useInProcessRateLimiter: false,
				tokenBucket: { tokenLimit: 5, tokensPerPeriod: 5, replenishmentPeriodMilliseconds: 3000 },
			},
		},
	},
	{
		name: "Deferral depth",
		hint: "One token every 3 seconds with no wait budget. Items keep coming back and cross the warning depth.",
		config: {
			itemCount: 30,
			minDelayMilliseconds: 100,
			maxDelayMilliseconds: 300,
			failCount: 0,
			area: BatchProcessArea.PayoutRequest,
			name: "Preset: deferral depth",
			rateLimit: {
				waitTimeoutMilliseconds: 0,
				useInProcessRateLimiter: false,
				tokenBucket: { tokenLimit: 1, tokensPerPeriod: 1, replenishmentPeriodMilliseconds: 3000 },
			},
		},
	},
	{
		name: "Concurrency squeeze",
		hint: "Two concurrent permits against 2-second items under a 5-second wait budget. Waits ride the observed hold time and the overflow defers on the concurrency profile.",
		config: {
			itemCount: 30,
			minDelayMilliseconds: 1500,
			maxDelayMilliseconds: 2500,
			failCount: 0,
			area: BatchProcessArea.PayoutRequest,
			name: "Preset: concurrency squeeze",
			rateLimit: {
				concurrency: { permitLimit: 2, leaseTimeoutMilliseconds: 120000 },
				waitTimeoutMilliseconds: 5000,
				useInProcessRateLimiter: false,
			},
		},
	},
	{
		name: "Bulk approval pacing",
		hint: "The bulk approval shape: 10 tokens a second with 10 concurrent permits and no window. Three hundred items start at a steady rate and finish in about 30 seconds.",
		config: {
			itemCount: 300,
			minDelayMilliseconds: 100,
			maxDelayMilliseconds: 500,
			failCount: 0,
			area: BatchProcessArea.PayoutRequest,
			name: "Preset: bulk approval pacing",
			rateLimit: {
				concurrency: { permitLimit: 10, leaseTimeoutMilliseconds: 30000 },
				waitTimeoutMilliseconds: 5000,
				useInProcessRateLimiter: false,
				tokenBucket: { tokenLimit: 10, tokensPerPeriod: 10, replenishmentPeriodMilliseconds: 1000 },
			},
		},
	},
];

function RouteComponent() {
	const queryClient = useQueryClient();
	const { data: capabilitiesResponse } = useGetOrganizationContextCapabilities();
	const canUseSensitiveDevTools = capabilitiesResponse?.data?.canUseSensitiveDevTools ?? false;
	const { payoutTransitionsEnabled, setPayoutTransitionsEnabled } = useDevToolsSettings();
	const { data: orgSettingsResponse } = useGetOrganizationSettings();
	const orgSettings = orgSettingsResponse?.data;
	const updateOrgSettingsMutation = useUpdateOrganizationSettings({
		mutation: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: getGetOrganizationSettingsQueryKey() });
			},
		},
	});

	const { data: fsmPlatformAccessResponse } = useGetApiAdminDevtoolsFsmPlatformAccess();
	const fsmPlatformAccess = fsmPlatformAccessResponse?.data ?? [];
	const setFsmPlatformAccessMutation = usePutApiAdminDevtoolsFsmPlatformAccess({
		mutation: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: getGetApiAdminDevtoolsFsmPlatformAccessQueryKey() });
				queryClient.invalidateQueries({ queryKey: getListFsmPlatformsQueryKey() });
			},
			onError: (err: AxiosError<ApiErrorResponse>) => {
				toast.error(err.response?.data?.error || "Failed to change integration visibility");
			},
		},
	});

	const resetVerificationMutation = useMutation({
		mutationFn: async () => {
			await axios.post("/api/admin/devtools/reset-identity-verification");
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
			queryClient.invalidateQueries({ queryKey: ["fundingAccounts"] });
			toast.success("Verification status has been reset");
		},
		onError: (err: AxiosError<ApiErrorResponse>) => {
			toast.error(err.response?.data?.error || "Failed to reset verification status");
		},
	});

	const skipVerificationMutation = useMutation({
		mutationFn: async () => {
			await axios.post("/api/admin/devtools/skip-identity-verification");
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
			toast.success("Verification has been marked as complete");
		},
		onError: (err: AxiosError<ApiErrorResponse>) => {
			toast.error(err.response?.data?.error || "Failed to skip verification");
		},
	});

	const resetTermsMutation = useMutation({
		mutationFn: async () => {
			await axios.delete("/api/admin/devtools/terms-of-service/acceptance");
		},
		onSuccess: () => {
			toast.success("Terms acceptance has been reset");
			window.location.reload();
		},
		onError: (err: AxiosError<ApiErrorResponse>) => {
			toast.error(err.response?.data?.error || "Failed to reset terms acceptance");
		},
	});

	const disconnectAstraMutation = useMutation({
		mutationFn: async () => {
			await axios.post("/api/admin/devtools/disconnect-astra-credential");
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
			queryClient.invalidateQueries({ queryKey: getGetApiOnboardingProgressQueryKey() });
			toast.success("Astra credential has been disconnected");
		},
		onError: (err: AxiosError<ApiErrorResponse>) => {
			toast.error(err.response?.data?.error || "Failed to disconnect Astra credential");
		},
	});

	const { data: onboardingProgressResponse } = useGetApiOnboardingProgress();
	const onboardingSteps = onboardingProgressResponse?.data?.steps ?? [];

	const deleteStepMutation = useMutation({
		mutationFn: async (stepName: string) => {
			await axios.delete(`/api/admin/devtools/onboarding-step/${stepName}`);
			await axios.patch("/api/users/me/settings", { onboardingProgressState: "Expanded" });
		},
		onSuccess: (_data, stepName) => {
			queryClient.invalidateQueries({ queryKey: getGetApiOnboardingProgressQueryKey() });
			queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
			queryClient.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
			toast.success(`Step "${stepName}" deleted`);
		},
		onError: (err: AxiosError<ApiErrorResponse>, stepName) => {
			toast.error(err.response?.data?.error || `Failed to delete step "${stepName}"`);
		},
	});

	const isVerificationLoading = resetVerificationMutation.isPending || skipVerificationMutation.isPending;

	const [adjustmentAmount, setAdjustmentAmount] = useState("");
	const [lastBalance, setLastBalance] = useState<number | null>(null);

	const handleAdjustmentAmountChange = (value: string) => {
		const cleaned = value.replace(/[^0-9.]/g, "");
		const parts = cleaned.split(".");
		if (parts.length > 2) return;
		if (parts[1]?.length > 2) return;
		setAdjustmentAmount(cleaned);
	};

	const adjustBalanceMutation = useMutation({
		mutationFn: async (type: "credit" | "debit") => {
			const response = await axios.post("/api/admin/devtools/adjust-balance", {
				amount: parseFloat(adjustmentAmount),
				type: type === "credit" ? "Credit" : "Debit",
			});
			return response.data as { availableBalance: number; pendingDeposits: number };
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
			queryClient.invalidateQueries({ queryKey: ["fundingTransactions"] });
			queryClient.invalidateQueries({ queryKey: ["dashboard"] });
			setLastBalance(data.availableBalance);
			setAdjustmentAmount("");
			toast.success(`Balance updated to $${data.availableBalance.toFixed(2)}`);
		},
		onError: (err: AxiosError<ApiErrorResponse>) => {
			toast.error(err.response?.data?.error || "Failed to adjust balance");
		},
	});

	const isAdjustDisabled = adjustBalanceMutation.isPending || !adjustmentAmount || parseFloat(adjustmentAmount) <= 0;

	const [simItemCount, setSimItemCount] = useState("10");
	const [simMinDelay, setSimMinDelay] = useState("500");
	const [simMaxDelay, setSimMaxDelay] = useState("2000");
	const [simFailCount, setSimFailCount] = useState("1");
	const [simArea, setSimArea] = useState<BatchProcessArea>(BatchProcessArea.PayoutRequest);
	const [simName, setSimName] = useState("");
	const [simRateLimitEnabled, setSimRateLimitEnabled] = useState(false);
	const [simWindowEnabled, setSimWindowEnabled] = useState(true);
	const [simWindowPermitLimit, setSimWindowPermitLimit] = useState("5");
	const [simWindowMilliseconds, setSimWindowMilliseconds] = useState("60000");
	const [simConcurrencyEnabled, setSimConcurrencyEnabled] = useState(true);
	const [simConcurrencyPermitLimit, setSimConcurrencyPermitLimit] = useState("5");
	const [simConcurrencyLeaseTimeoutMilliseconds, setSimConcurrencyLeaseTimeoutMilliseconds] = useState("120000");
	const [simRateLimitWaitTimeoutMilliseconds, setSimRateLimitWaitTimeoutMilliseconds] = useState("5000");
	const [simUseInProcessRateLimiter, setSimUseInProcessRateLimiter] = useState(false);
	const [simTokenBucketEnabled, setSimTokenBucketEnabled] = useState(false);
	const [simTokenLimit, setSimTokenLimit] = useState("2");
	const [simTokensPerPeriod, setSimTokensPerPeriod] = useState("1");
	const [simReplenishmentPeriodMilliseconds, setSimReplenishmentPeriodMilliseconds] = useState("1000");
	const [simPercentilesEnabled, setSimPercentilesEnabled] = useState(false);
	const [simP50, setSimP50] = useState("550");
	const [simP75, setSimP75] = useState("850");
	const [simP90, setSimP90] = useState("1900");
	const [simP95, setSimP95] = useState("3070");

	const simulateBatchProcessMutation = useMutation({
		mutationFn: async () => {
			const payload: SimulateBatchProcessRequest = {
				itemCount: Number(simItemCount),
				minDelayMilliseconds: Number(simMinDelay),
				maxDelayMilliseconds: Number(simMaxDelay),
				delayPercentiles: simPercentilesEnabled
					? {
						p50Milliseconds: Number(simP50),
						p75Milliseconds: Number(simP75),
						p90Milliseconds: Number(simP90),
						p95Milliseconds: Number(simP95),
					}
					: undefined,
				failCount: Number(simFailCount),
				area: simArea,
				name: simName.trim() || null,
				rateLimit: simRateLimitEnabled
					? {
						waitTimeoutMilliseconds: Number(simRateLimitWaitTimeoutMilliseconds),
						useInProcessRateLimiter: simUseInProcessRateLimiter,
						window: simWindowEnabled
							? {
								permitLimit: Number(simWindowPermitLimit),
								windowMilliseconds: Number(simWindowMilliseconds),
							}
							: undefined,
						concurrency: simConcurrencyEnabled
							? {
								permitLimit: Number(simConcurrencyPermitLimit),
								leaseTimeoutMilliseconds: Number(simConcurrencyLeaseTimeoutMilliseconds),
							}
							: undefined,
						tokenBucket: simTokenBucketEnabled
							? {
								tokenLimit: Number(simTokenLimit),
								tokensPerPeriod: Number(simTokensPerPeriod),
								replenishmentPeriodMilliseconds: Number(simReplenishmentPeriodMilliseconds),
							}
							: undefined,
					}
					: undefined,
			};
			await axios.post("/api/admin/devtools/batch-process-simulation", payload);
		},
		onError: (err: AxiosError<ApiErrorResponse>) => {
			toast.error(err.response?.data?.error || "Failed to start simulation");
		},
	});

	const itemCount = Number(simItemCount);
	const minDelay = Number(simMinDelay);
	const maxDelay = Number(simMaxDelay);
	const failCount = Number(simFailCount);
	const windowPermitLimit = Number(simWindowPermitLimit);
	const windowMilliseconds = Number(simWindowMilliseconds);
	const concurrencyPermitLimit = Number(simConcurrencyPermitLimit);
	const concurrencyLeaseTimeoutMilliseconds = Number(simConcurrencyLeaseTimeoutMilliseconds);
	const rateLimitWaitTimeoutMilliseconds = Number(simRateLimitWaitTimeoutMilliseconds);
	const isSimRateLimitInvalid =
		simRateLimitEnabled && (
			!Number.isInteger(rateLimitWaitTimeoutMilliseconds) || rateLimitWaitTimeoutMilliseconds < 0 || rateLimitWaitTimeoutMilliseconds > 600000
		);
	const isSimWindowInvalid =
		simRateLimitEnabled && simWindowEnabled && (
			!Number.isInteger(windowPermitLimit) || windowPermitLimit < 1 || windowPermitLimit > 500 ||
			!Number.isInteger(windowMilliseconds) || windowMilliseconds < 1 || windowMilliseconds > 600000
		);
	const isSimConcurrencyInvalid =
		simRateLimitEnabled && simConcurrencyEnabled && (
			!Number.isInteger(concurrencyPermitLimit) || concurrencyPermitLimit < 1 || concurrencyPermitLimit > 500 ||
			!Number.isInteger(concurrencyLeaseTimeoutMilliseconds) || concurrencyLeaseTimeoutMilliseconds < 1 || concurrencyLeaseTimeoutMilliseconds > 600000
		);
	const tokenLimit = Number(simTokenLimit);
	const tokensPerPeriod = Number(simTokensPerPeriod);
	const replenishmentPeriodMilliseconds = Number(simReplenishmentPeriodMilliseconds);
	const isSimTokenBucketInvalid =
		simRateLimitEnabled && simTokenBucketEnabled && (
			!Number.isInteger(tokenLimit) || tokenLimit < 1 || tokenLimit > 500 ||
			!Number.isInteger(tokensPerPeriod) || tokensPerPeriod < 1 || tokensPerPeriod > 500 ||
			!Number.isInteger(replenishmentPeriodMilliseconds) || replenishmentPeriodMilliseconds < 1 || replenishmentPeriodMilliseconds > 600000
		);
	const p50Milliseconds = Number(simP50);
	const p75Milliseconds = Number(simP75);
	const p90Milliseconds = Number(simP90);
	const p95Milliseconds = Number(simP95);
	const isSimPercentilesInvalid =
		simPercentilesEnabled && (
			!Number.isInteger(p50Milliseconds) || p50Milliseconds < minDelay ||
			!Number.isInteger(p75Milliseconds) || p75Milliseconds < p50Milliseconds ||
			!Number.isInteger(p90Milliseconds) || p90Milliseconds < p75Milliseconds ||
			!Number.isInteger(p95Milliseconds) || p95Milliseconds < p90Milliseconds || p95Milliseconds > maxDelay
		);
	const isSimInvalid =
		!Number.isInteger(itemCount) || itemCount < 1 || itemCount > 500 ||
		minDelay < 0 || maxDelay < minDelay ||
		failCount < 0 || failCount > itemCount ||
		isSimPercentilesInvalid ||
		isSimRateLimitInvalid ||
		isSimWindowInvalid ||
		isSimConcurrencyInvalid ||
		isSimTokenBucketInvalid;
	const isSimDisabled = simulateBatchProcessMutation.isPending || isSimInvalid;

	const [simConfigJson, setSimConfigJson] = useState("");
	const applySimConfigObject = (config: SimulateBatchProcessRequest) => {
		if (config.itemCount !== undefined) setSimItemCount(String(config.itemCount));
		if (config.minDelayMilliseconds !== undefined) setSimMinDelay(String(config.minDelayMilliseconds));
		if (config.maxDelayMilliseconds !== undefined) setSimMaxDelay(String(config.maxDelayMilliseconds));
		if (config.failCount !== undefined) setSimFailCount(String(config.failCount));
		if (config.area !== undefined) setSimArea(config.area);
		if (config.name !== undefined) setSimName(config.name ?? "");
		if (config.delayPercentiles !== undefined) setSimPercentilesEnabled(config.delayPercentiles != null);
		if (config.delayPercentiles != null) {
			if (config.delayPercentiles.p50Milliseconds !== undefined) setSimP50(String(config.delayPercentiles.p50Milliseconds));
			if (config.delayPercentiles.p75Milliseconds !== undefined) setSimP75(String(config.delayPercentiles.p75Milliseconds));
			if (config.delayPercentiles.p90Milliseconds !== undefined) setSimP90(String(config.delayPercentiles.p90Milliseconds));
			if (config.delayPercentiles.p95Milliseconds !== undefined) setSimP95(String(config.delayPercentiles.p95Milliseconds));
		}
		if (config.rateLimit !== undefined) setSimRateLimitEnabled(config.rateLimit != null);
		if (config.rateLimit != null) {
			if (config.rateLimit.window !== undefined) setSimWindowEnabled(config.rateLimit.window != null);
			if (config.rateLimit.window != null) {
				if (config.rateLimit.window.permitLimit !== undefined) setSimWindowPermitLimit(String(config.rateLimit.window.permitLimit));
				if (config.rateLimit.window.windowMilliseconds !== undefined) setSimWindowMilliseconds(String(config.rateLimit.window.windowMilliseconds));
			}
			if (config.rateLimit.concurrency !== undefined) setSimConcurrencyEnabled(config.rateLimit.concurrency != null);
			if (config.rateLimit.concurrency != null) {
				if (config.rateLimit.concurrency.permitLimit !== undefined) setSimConcurrencyPermitLimit(String(config.rateLimit.concurrency.permitLimit));
				if (config.rateLimit.concurrency.leaseTimeoutMilliseconds !== undefined) setSimConcurrencyLeaseTimeoutMilliseconds(String(config.rateLimit.concurrency.leaseTimeoutMilliseconds));
			}
			if (config.rateLimit.waitTimeoutMilliseconds !== undefined) setSimRateLimitWaitTimeoutMilliseconds(String(config.rateLimit.waitTimeoutMilliseconds));
			if (config.rateLimit.useInProcessRateLimiter !== undefined) setSimUseInProcessRateLimiter(Boolean(config.rateLimit.useInProcessRateLimiter));
			if (config.rateLimit.tokenBucket !== undefined) setSimTokenBucketEnabled(config.rateLimit.tokenBucket != null);
			if (config.rateLimit.tokenBucket != null) {
				if (config.rateLimit.tokenBucket.tokenLimit !== undefined) setSimTokenLimit(String(config.rateLimit.tokenBucket.tokenLimit));
				if (config.rateLimit.tokenBucket.tokensPerPeriod !== undefined) setSimTokensPerPeriod(String(config.rateLimit.tokenBucket.tokensPerPeriod));
				if (config.rateLimit.tokenBucket.replenishmentPeriodMilliseconds !== undefined) setSimReplenishmentPeriodMilliseconds(String(config.rateLimit.tokenBucket.replenishmentPeriodMilliseconds));
			}
		}
	};
	const applySimConfig = () => {
		try {
			applySimConfigObject(JSON.parse(simConfigJson) as SimulateBatchProcessRequest);
			toast.success("Simulation config applied");
		} catch {
			toast.error("Invalid simulation config JSON");
		}
	};
	const applySimPreset = (preset: SimPreset) => {
		setSimConfigJson(JSON.stringify(preset.config, null, 2));
		applySimConfigObject(preset.config);
		setSimPercentilesEnabled(preset.config.delayPercentiles != null);
		setSimRateLimitEnabled(preset.config.rateLimit != null);
		setSimWindowEnabled(preset.config.rateLimit?.window != null);
		setSimConcurrencyEnabled(preset.config.rateLimit?.concurrency != null);
		setSimTokenBucketEnabled(preset.config.rateLimit?.tokenBucket != null);
		toast.success(`${preset.name} preset applied`);
	};

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Dev Tools"
				parent={{ url: "/dashboard", label: "Home" }}
			/>
			<PageContentContainer>
				<div className="flex flex-col gap-4 max-w-lg">
					<section className="flex flex-col bg-white p-5">
						<AriaSwitch
							isSelected={payoutTransitionsEnabled}
							onChange={setPayoutTransitionsEnabled}
							className="flex items-start gap-3"
						>
							{({ isSelected, isHovered }) => (
								<>
									<div className={cx(
										"h-7 w-14 shrink-0 rounded-full p-0.5 transition duration-150 ease-linear cursor-pointer",
										isSelected ? "bg-brand-secondary" : "bg-gray-300",
										isSelected && isHovered && "bg-brand-secondary_hover",
									)}>
										<div className={cx(
											"size-6 rounded-full bg-fg-white shadow-sm transition-transform duration-150 ease-in-out",
											isSelected && "translate-x-7",
										)} />
									</div>
									<div className="flex flex-col gap-0.5">
										<p className="text-md font-medium text-secondary select-none">Payout Transitions</p>
										<span className="text-md text-tertiary">Show transition actions on payout rows in the payouts queue</span>
									</div>
								</>
							)}
						</AriaSwitch>
					</section>

					<section className="border border-gray-200 rounded-lg p-4 bg-white">
						<h4 className="font-semibold text-gray-900 mb-2">Direct Payout Placement</h4>
						<p className="text-sm text-gray-600 mb-4">Control where the "Direct Payout" card appears.</p>
						<div className="space-y-3">
							<AriaSwitch isSelected={orgSettings?.showDirectPayoutOnHome ?? true} onChange={(enabled) => orgSettings && updateOrgSettingsMutation.mutate({ data: { ...orgSettings, showDirectPayoutOnHome: enabled } })} className="flex items-start gap-3">
								{({ isSelected, isHovered }) => (
									<>
										<div className={cx("h-7 w-14 shrink-0 rounded-full p-0.5 transition duration-150 ease-linear cursor-pointer", isSelected ? "bg-brand-secondary" : "bg-gray-300", isSelected && isHovered && "bg-brand-secondary_hover")}>
											<div className={cx("size-6 rounded-full bg-fg-white shadow-sm transition-transform duration-150 ease-in-out", isSelected && "translate-x-7")} />
										</div>
										<div className="flex flex-col gap-0.5">
											<p className="text-md font-medium text-secondary select-none">Show on Home</p>
											<span className="text-md text-tertiary">Display the pay card on the Home screen</span>
										</div>
									</>
								)}
							</AriaSwitch>
							<AriaSwitch isSelected={orgSettings?.showDirectPayoutOnPay ?? true} onChange={(enabled) => orgSettings && updateOrgSettingsMutation.mutate({ data: { ...orgSettings, showDirectPayoutOnPay: enabled } })} className="flex items-start gap-3">
								{({ isSelected, isHovered }) => (
									<>
										<div className={cx("h-7 w-14 shrink-0 rounded-full p-0.5 transition duration-150 ease-linear cursor-pointer", isSelected ? "bg-brand-secondary" : "bg-gray-300", isSelected && isHovered && "bg-brand-secondary_hover")}>
											<div className={cx("size-6 rounded-full bg-fg-white shadow-sm transition-transform duration-150 ease-in-out", isSelected && "translate-x-7")} />
										</div>
										<div className="flex flex-col gap-0.5">
											<p className="text-md font-medium text-secondary select-none">Show on Pay</p>
											<span className="text-md text-tertiary">Display the pay card on the Pay screen</span>
										</div>
									</>
								)}
							</AriaSwitch>
						</div>
					</section>

					<section className="border border-gray-200 rounded-lg p-4 bg-white">
						<h4 className="font-semibold text-gray-900 mb-2">Debit Card Payouts</h4>
						<p className="text-sm text-gray-600 mb-4">Reveal debit card linking and the payout method choice to this organization's talent members.</p>
						<div className="space-y-3">
							<AriaSwitch isSelected={orgSettings?.pushToCardEnabled ?? false} onChange={(enabled) => orgSettings && updateOrgSettingsMutation.mutate({ data: { ...orgSettings, pushToCardEnabled: enabled } })} className="flex items-start gap-3">
								{({ isSelected, isHovered }) => (
									<>
										<div className={cx("h-7 w-14 shrink-0 rounded-full p-0.5 transition duration-150 ease-linear cursor-pointer", isSelected ? "bg-brand-secondary" : "bg-gray-300", isSelected && isHovered && "bg-brand-secondary_hover")}>
											<div className={cx("size-6 rounded-full bg-fg-white shadow-sm transition-transform duration-150 ease-in-out", isSelected && "translate-x-7")} />
										</div>
										<div className="flex flex-col gap-0.5">
											<p className="text-md font-medium text-secondary select-none">Enable debit card payouts</p>
											<span className="text-md text-tertiary">Show Debit Cards and Payout Method in the talent app</span>
										</div>
									</>
								)}
							</AriaSwitch>
						</div>
					</section>

					<section className="border border-gray-200 rounded-lg p-4 bg-white">
						<h4 className="font-semibold text-gray-900 mb-2">Payout Policies</h4>
						<p className="text-sm text-gray-600 mb-4">Reveal the Payout Policies settings page and payout terms on the roster to this organization. Off until the organization is ready to schedule its payouts.</p>
						<div className="space-y-3">
							<AriaSwitch isSelected={orgSettings?.payoutPolicyEnabled ?? false} onChange={(enabled) => orgSettings && updateOrgSettingsMutation.mutate({ data: { ...orgSettings, payoutPolicyEnabled: enabled } })} className="flex items-start gap-3">
								{({ isSelected, isHovered }) => (
									<>
										<div className={cx("h-7 w-14 shrink-0 rounded-full p-0.5 transition duration-150 ease-linear cursor-pointer", isSelected ? "bg-brand-secondary" : "bg-gray-300", isSelected && isHovered && "bg-brand-secondary_hover")}>
											<div className={cx("size-6 rounded-full bg-fg-white shadow-sm transition-transform duration-150 ease-in-out", isSelected && "translate-x-7")} />
										</div>
										<div className="flex flex-col gap-0.5">
											<p className="text-md font-medium text-secondary select-none">Enable payout policy</p>
											<span className="text-md text-tertiary">Show Payout Policies in Settings and payout terms on the roster</span>
										</div>
									</>
								)}
							</AriaSwitch>
						</div>
					</section>

					<section className="border border-gray-200 rounded-lg p-4 bg-white">
						<h4 className="font-semibold text-gray-900 mb-2">Integration Visibility</h4>
						<p className="text-sm text-gray-600 mb-4">Control which integrations this organization sees. Generally available and coming soon integrations are visible unless you turn them off; private integrations are hidden unless you turn them on. An integration the organization cannot see is also refused by its connection endpoints.</p>
						<div className="space-y-3">
							{fsmPlatformAccess.map((access) => (
								<AriaSwitch key={access.platform} isSelected={access.isVisible} onChange={(isVisible) => setFsmPlatformAccessMutation.mutate({ data: { platform: access.platform, isVisible } })} className="flex items-start gap-3">
									{({ isSelected, isHovered }) => (
										<>
											<div className={cx("h-7 w-14 shrink-0 rounded-full p-0.5 transition duration-150 ease-linear cursor-pointer", isSelected ? "bg-brand-secondary" : "bg-gray-300", isSelected && isHovered && "bg-brand-secondary_hover")}>
												<div className={cx("size-6 rounded-full bg-fg-white shadow-sm transition-transform duration-150 ease-in-out", isSelected && "translate-x-7")} />
											</div>
											<div className="flex flex-col gap-0.5">
												<p className="text-md font-medium text-secondary select-none">{fsmPlatformLabels[access.platform]}</p>
												<span className="text-md text-tertiary">{fsmPlatformStatusLabels[access.status]}{access.isOverridden ? " · overridden for this organization" : ""}</span>
											</div>
										</>
									)}
								</AriaSwitch>
							))}
						</div>
					</section>

					<section className="border border-gray-200 rounded-lg p-4 bg-white">
						<h4 className="font-semibold text-gray-900 mb-2">Batch Process Simulation</h4>
						<p className="text-sm text-gray-600 mb-4">Create a batch of no-op items that take real wall-clock time to process, to exercise the batch process progress widget.</p>
						<div className="flex flex-col gap-2 mb-4">
							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-gray-700">Paste config (JSON, request shape)</span>
								<textarea rows={3} value={simConfigJson} onChange={(e) => setSimConfigJson(e.target.value)} placeholder='{"itemCount": 50, "minDelayMilliseconds": 400, "maxDelayMilliseconds": 1200, "rateLimit": {"concurrency": {"permitLimit": 5}, "waitTimeoutMilliseconds": 5000, "useInProcessRateLimiter": false}}' spellCheck={false} className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
							</label>
							<div>
								<Button onClick={applySimConfig} color="secondary" size="sm" isDisabled={!simConfigJson.trim()}>Apply Config</Button>
							</div>
							<span className="text-xs font-medium text-gray-700">Presets</span>
							<div className="flex flex-wrap gap-2">
								{simPresets.map((preset) => (
									<span key={preset.name} title={preset.hint}>
										<Button onClick={() => applySimPreset(preset)} color="secondary" size="sm">{preset.name}</Button>
									</span>
								))}
							</div>
						</div>
						<div className="grid grid-cols-2 gap-3 mb-4">
							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-gray-700">Item count</span>
								<input type="number" min={1} max={500} value={simItemCount} onChange={(e) => setSimItemCount(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-gray-700">Failing items</span>
								<input type="number" min={0} value={simFailCount} onChange={(e) => setSimFailCount(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-gray-700">Min delay (ms)</span>
								<input type="number" min={0} value={simMinDelay} onChange={(e) => setSimMinDelay(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-gray-700">Max delay (ms)</span>
								<input type="number" min={0} value={simMaxDelay} onChange={(e) => setSimMaxDelay(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-gray-700">Area</span>
								<select value={simArea} onChange={(e) => setSimArea(e.target.value as BatchProcessArea)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500">
									<option value={BatchProcessArea.PayoutRequest}>Payout request</option>
									<option value={BatchProcessArea.Fsm}>FSM</option>
								</select>
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-gray-700">Name (optional)</span>
								<input type="text" value={simName} onChange={(e) => setSimName(e.target.value)} placeholder="Simulated batch process" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
							</label>
						</div>
						<div className="border-t border-gray-200 pt-4 mb-4">
							<Toggle size="sm" className="w-full" label="Percentile delays" hint="Sample item delays from a distribution shaped by these percentiles instead of uniformly between min and max." isSelected={simPercentilesEnabled} onChange={setSimPercentilesEnabled} />
							{simPercentilesEnabled && (
								<div className="grid grid-cols-2 gap-3 mt-3">
									<label className="flex flex-col gap-1">
										<span className="text-xs font-medium text-gray-700">p50 (ms)</span>
										<input type="number" min={0} value={simP50} onChange={(e) => setSimP50(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
									</label>
									<label className="flex flex-col gap-1">
										<span className="text-xs font-medium text-gray-700">p75 (ms)</span>
										<input type="number" min={0} value={simP75} onChange={(e) => setSimP75(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
									</label>
									<label className="flex flex-col gap-1">
										<span className="text-xs font-medium text-gray-700">p90 (ms)</span>
										<input type="number" min={0} value={simP90} onChange={(e) => setSimP90(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
									</label>
									<label className="flex flex-col gap-1">
										<span className="text-xs font-medium text-gray-700">p95 (ms)</span>
										<input type="number" min={0} value={simP95} onChange={(e) => setSimP95(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
									</label>
								</div>
							)}
						</div>
						<div className="border-t border-gray-200 pt-4 mb-4">
							<Toggle size="sm" className="w-full" label="Rate limit" hint="Acquire permits from the rate limiter per item. Denied items defer instead of failing." isSelected={simRateLimitEnabled} onChange={setSimRateLimitEnabled} />
							{simRateLimitEnabled && (
								<div className="grid grid-cols-2 gap-3 mt-3">
									<label className="flex flex-col gap-1 col-span-2">
										<span className="text-xs font-medium text-gray-700">Wait timeout (ms)</span>
										<input type="number" min={0} max={600000} value={simRateLimitWaitTimeoutMilliseconds} onChange={(e) => setSimRateLimitWaitTimeoutMilliseconds(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
									</label>
									<div className="col-span-2">
										<Toggle size="sm" className="w-full" label="In-process limiter" hint="Use the in-process rate limiter instead of the Redis-backed distributed one." isSelected={simUseInProcessRateLimiter} onChange={setSimUseInProcessRateLimiter} />
									</div>
									<div className="col-span-2">
										<Toggle size="sm" className="w-full" label="Window" hint="Cap how many items start within a sliding window." isSelected={simWindowEnabled} onChange={setSimWindowEnabled} />
									</div>
									{simWindowEnabled && (
										<>
											<label className="flex flex-col gap-1">
												<span className="text-xs font-medium text-gray-700">Window permits</span>
												<input type="number" min={1} max={500} value={simWindowPermitLimit} onChange={(e) => setSimWindowPermitLimit(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
											</label>
											<label className="flex flex-col gap-1">
												<span className="text-xs font-medium text-gray-700">Window (ms)</span>
												<input type="number" min={1} max={600000} value={simWindowMilliseconds} onChange={(e) => setSimWindowMilliseconds(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
											</label>
										</>
									)}
									<div className="col-span-2">
										<Toggle size="sm" className="w-full" label="Concurrency" hint="Cap how many items hold a permit at once. A permit is released when the item finishes or its lease times out." isSelected={simConcurrencyEnabled} onChange={setSimConcurrencyEnabled} />
									</div>
									{simConcurrencyEnabled && (
										<>
											<label className="flex flex-col gap-1">
												<span className="text-xs font-medium text-gray-700">Concurrent permits</span>
												<input type="number" min={1} max={500} value={simConcurrencyPermitLimit} onChange={(e) => setSimConcurrencyPermitLimit(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
											</label>
											<label className="flex flex-col gap-1">
												<span className="text-xs font-medium text-gray-700">Lease timeout (ms)</span>
												<input type="number" min={1} max={600000} value={simConcurrencyLeaseTimeoutMilliseconds} onChange={(e) => setSimConcurrencyLeaseTimeoutMilliseconds(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
											</label>
										</>
									)}
									<div className="col-span-2">
										<Toggle size="sm" className="w-full" label="Token bucket" hint="Pace items at a steady replenishment rate instead of letting them start in a burst." isSelected={simTokenBucketEnabled} onChange={setSimTokenBucketEnabled} />
									</div>
									{simTokenBucketEnabled && (
										<>
											<label className="flex flex-col gap-1">
												<span className="text-xs font-medium text-gray-700">Token limit</span>
												<input type="number" min={1} max={500} value={simTokenLimit} onChange={(e) => setSimTokenLimit(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
											</label>
											<label className="flex flex-col gap-1">
												<span className="text-xs font-medium text-gray-700">Tokens per period</span>
												<input type="number" min={1} max={500} value={simTokensPerPeriod} onChange={(e) => setSimTokensPerPeriod(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
											</label>
											<label className="flex flex-col gap-1">
												<span className="text-xs font-medium text-gray-700">Replenishment period (ms)</span>
												<input type="number" min={1} max={600000} value={simReplenishmentPeriodMilliseconds} onChange={(e) => setSimReplenishmentPeriodMilliseconds(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
											</label>
										</>
									)}
								</div>
							)}
						</div>
						<Button onClick={() => simulateBatchProcessMutation.mutate()} color="secondary" size="sm" isLoading={simulateBatchProcessMutation.isPending} isDisabled={isSimDisabled} iconLeading={LayersThree01}>Start Simulation</Button>
						<div className="flex items-start gap-2 p-3 mt-4 bg-amber-50 border border-amber-200 rounded-lg">
							<AlertCircle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
							<p className="text-sm text-amber-800">Runs the real batch pipeline with no-op work — set min and max delay equal for fixed timing. A tight rate limit budget parks items in Deferred until the window frees up.</p>
						</div>
					</section>

					{canUseSensitiveDevTools && (
						<section className="border border-gray-200 rounded-lg p-4 bg-white">
							<h4 className="font-semibold text-gray-900 mb-2">Balance Adjustment</h4>
							<p className="text-sm text-gray-600 mb-4">Add or remove funds from the current organization's balance.</p>
							<div className="space-y-4 mb-4">
								<div className="relative">
									<span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
									<input
										type="text"
										inputMode="decimal"
										value={adjustmentAmount}
										onChange={(e) => handleAdjustmentAmountChange(e.target.value)}
										placeholder="0.00"
										className="w-full pl-8 pr-4 py-2 text-lg font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
									/>
								</div>
								<div className="flex gap-3">
									<Button onClick={() => adjustBalanceMutation.mutate("credit")} color="secondary" size="sm" isLoading={adjustBalanceMutation.isPending} isDisabled={isAdjustDisabled} iconLeading={Plus}>Add Funds</Button>
									<Button onClick={() => adjustBalanceMutation.mutate("debit")} color="secondary" size="sm" isLoading={adjustBalanceMutation.isPending} isDisabled={isAdjustDisabled} iconLeading={Minus}>Remove Funds</Button>
								</div>
								{lastBalance !== null && (
									<p className="text-sm text-gray-600">Available balance: <span className="font-semibold text-gray-900">${lastBalance.toFixed(2)}</span></p>
								)}
							</div>
							<div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
								<AlertCircle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
								<p className="text-sm text-amber-800">This creates synthetic funding transactions. No real money moves.</p>
							</div>
						</section>
					)}

					{canUseSensitiveDevTools && <FundingAccountBalanceCheck />}

					{canUseSensitiveDevTools && (
						<section className="border border-gray-200 rounded-lg p-4 bg-white">
							<h4 className="font-semibold text-gray-900 mb-2">Identity Verification</h4>
							<p className="text-sm text-gray-600 mb-4">Manage identity verification status for testing.</p>
							<div className="flex gap-3 mb-4">
								<Button onClick={() => resetVerificationMutation.mutate()} color="secondary" size="sm" isLoading={resetVerificationMutation.isPending} isDisabled={isVerificationLoading} iconLeading={RefreshCcw01}>Reset</Button>
								<Button onClick={() => skipVerificationMutation.mutate()} color="secondary" size="sm" isLoading={skipVerificationMutation.isPending} isDisabled={isVerificationLoading} iconLeading={Check}>Skip</Button>
							</div>
							<div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
								<AlertCircle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
								<p className="text-sm text-amber-800">These actions modify your organization's verification status. Use with caution during testing.</p>
							</div>
						</section>
					)}

					{canUseSensitiveDevTools && (
						<section className="border border-gray-200 rounded-lg p-4 bg-white">
							<h4 className="font-semibold text-gray-900 mb-2">Astra Credential</h4>
							<p className="text-sm text-gray-600 mb-4">Disconnect the Astra OAuth credential to re-test the authorization flow.</p>
							<Button onClick={() => disconnectAstraMutation.mutate()} color="secondary" size="sm" isLoading={disconnectAstraMutation.isPending} isDisabled={disconnectAstraMutation.isPending} iconLeading={LinkBroken01}>Disconnect</Button>
						</section>
					)}

					<section className="border border-gray-200 rounded-lg p-4 bg-white">
						<h4 className="font-semibold text-gray-900 mb-2">Onboarding Progress</h4>
						<p className="text-sm text-gray-600 mb-4">Delete individual onboarding steps to reset progress bar state for testing.</p>
						{onboardingSteps.length === 0 ? (
							<p className="text-sm text-gray-500 italic">No onboarding steps recorded.</p>
						) : (
							<div className="space-y-2">
								{onboardingSteps.map((step) => (
									<div key={step.stepName} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
										<div className="flex flex-col">
											<span className="text-sm font-medium text-gray-900">{step.stepName}</span>
											<span className="text-xs text-gray-500">{step.occurredAt ? formatLocalDate(step.occurredAt, "datetimeWithYear") : ""}</span>
										</div>
										<Button onClick={() => deleteStepMutation.mutate(step.stepName!)} color="secondary-destructive" size="sm" isLoading={deleteStepMutation.isPending && deleteStepMutation.variables === step.stepName} isDisabled={deleteStepMutation.isPending} iconLeading={Trash01}>Delete</Button>
									</div>
								))}
							</div>
						)}
					</section>

					<section className="border border-gray-200 rounded-lg p-4 bg-white">
						<h4 className="font-semibold text-gray-900 mb-2">Terms & Conditions</h4>
						<p className="text-sm text-gray-600 mb-4">Reset terms acceptance to re-test the acceptance flow.</p>
						<Button onClick={() => resetTermsMutation.mutate()} color="secondary" size="sm" isLoading={resetTermsMutation.isPending} isDisabled={resetTermsMutation.isPending} iconLeading={RefreshCcw01}>Reset Terms Acceptance</Button>
					</section>

					<SuperiorPumpEnrollment />

					<SkimmerManualConnection />

					{canUseSensitiveDevTools && <BusinessVerificationReview />}

					{canUseSensitiveDevTools && <SuspectedDuplicateUserQueue />}

					{canUseSensitiveDevTools && <JobTriggers />}

					{canUseSensitiveDevTools && !isLocal && <RecycleJobsHost />}
				</div>
			</PageContentContainer>
		</AdminLayout>
	);
}